import { useEffect, useState } from "react";
import { Check, X, CalendarClock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { FadeUp } from "../components/Reveal";
import { StatusBadge } from "../portal/PortalDashboard";

const FILTERS = ["all", "pending", "approved", "completed", "declined", "cancelled"];

export default function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState("all");
  const [rescheduling, setRescheduling] = useState(null);
  const [rDate, setRDate] = useState("");
  const [rTime, setRTime] = useState("");
  const [services, setServices] = useState([]);
  const [overrides, setOverrides] = useState({});

  const load = () => api.get("/bookings").then((r) => setBookings(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/services?all=true").then((r) => setServices(r.data)).catch(() => {}); }, []);

  const defaultPercent = (b) => services.find((s) => s.service_id === b.service_id)?.deposit_percent ?? 50;
  const approve = (b) => {
    const raw = overrides[b.booking_id];
    const extra = raw !== undefined && raw !== "" ? { deposit_percent: Math.max(0, Math.min(100, Number(raw))) } : {};
    return act(b.booking_id, "approve", extra);
  };

  const act = async (id, action, extra = {}) => {
    try {
      await api.patch(`/bookings/${id}`, { action, ...extra });
      toast.success(action.includes("cash") ? "Updated — client notified by text" : `Booking ${action}d — client notified by text`);
      setRescheduling(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const shown = filter === "all" ? bookings : bookings.filter((b) => b.status === filter);

  return (
    <div data-testid="admin-bookings">
      <FadeUp>
        <h1 className="font-serif text-3xl sm:text-4xl text-obsidian">Bookings</h1>
        <p className="text-sm text-slate-500 mt-2">Approve, decline or reschedule — clients get a text the moment you decide.</p>
      </FadeUp>

      <div className="flex flex-wrap gap-2 mt-8" data-testid="booking-filters">
        {FILTERS.map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
            className={`px-4 py-2 rounded-full text-xs font-mono uppercase tracking-widest transition-colors ${
              filter === f ? "bg-obsidian text-gold" : "bg-white text-slate-500 border border-purple-100 hover:border-gold"
            }`}>
            {f}
          </button>
        ))}
      </div>

      <div className="mt-6 space-y-4">
        {shown.map((b) => (
          <FadeUp key={b.booking_id} className="bg-white rounded-2xl border border-purple-100 p-6" data-testid={`admin-booking-${b.booking_id}`}>
            <div className="flex flex-wrap items-start gap-4">
              <div className="flex-1 min-w-[240px]">
                <p className="font-medium text-obsidian">{b.client_name} <span className="text-slate-400 font-normal">· {b.client_email}</span></p>
                <p className="font-serif text-xl mt-1">{b.service_name}</p>
                <p className="text-sm text-slate-500 mt-1">{b.date} at {b.time} · {b.location_name} · <span className="text-purple-700 font-medium">${b.price}</span></p>
                {b.notes && <p className="text-xs text-slate-400 mt-2 italic">"{b.notes}"</p>}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <StatusBadge status={b.status} />
                {b.status === "pending" && b.cash_deposit && (
                  <span className="text-[10px] font-mono uppercase tracking-widest px-2.5 py-1 rounded-full bg-amber-100 text-amber-800" data-testid={`admin-cash-pref-${b.booking_id}`}>Wants to pay deposit in cash</span>
                )}
                {b.status === "approved" && b.deposit_amount > 0 && (
                  <span className={`text-[10px] font-mono uppercase tracking-widest px-2.5 py-1 rounded-full ${["paid", "cash"].includes(b.deposit_status) ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`} data-testid={`admin-deposit-${b.booking_id}`}>
                    {b.deposit_status === "paid" ? `${b.deposit_percent ?? 50}% deposit paid` : b.deposit_status === "cash" ? "Cash deposit received" : b.deposit_status === "cash_pending" ? `Cash requested · $${b.deposit_amount}` : `${b.deposit_percent ?? 50}% deposit due $${b.deposit_amount}`}
                  </span>
                )}
                {b.status === "approved" && b.deposit_status === "waived" && (
                  <span className="text-[10px] font-mono uppercase tracking-widest px-2.5 py-1 rounded-full bg-slate-100 text-slate-600" data-testid={`admin-deposit-${b.booking_id}`}>No deposit</span>
                )}
              </div>
            </div>

            {b.status === "approved" && b.deposit_status === "cash_pending" && (
              <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 flex flex-wrap items-center gap-3" data-testid={`cash-review-${b.booking_id}`}>
                <p className="text-xs text-amber-900 flex-1 min-w-[220px]">{b.client_name} says you agreed to a <span className="font-semibold">${b.deposit_amount} cash deposit</span>. Confirm once received, or decline to send them back to card payment.</p>
                <button onClick={() => act(b.booking_id, "confirm_cash")} data-testid={`confirm-cash-${b.booking_id}`} className="px-4 py-2 rounded-full bg-emerald-600 text-white text-xs hover:bg-emerald-700 transition-colors">Cash received</button>
                <button onClick={() => act(b.booking_id, "reject_cash")} data-testid={`reject-cash-${b.booking_id}`} className="px-4 py-2 rounded-full border border-amber-300 text-amber-900 text-xs hover:bg-amber-100 transition-colors">Decline</button>
              </div>
            )}

            {["pending", "approved"].includes(b.status) && (
              <div className="flex flex-wrap gap-2 mt-5 pt-5 border-t border-purple-50">
                {b.status === "pending" && (
                  <>
                    <button onClick={() => approve(b)} data-testid={`approve-${b.booking_id}`}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-emerald-600 text-white text-xs hover:bg-emerald-700 transition-colors">
                      <Check size={13} /> Approve
                    </button>
                    <label className="flex items-center gap-1.5 text-xs text-slate-500 bg-lilac-mist rounded-full pl-3 pr-2 py-1" data-testid={`deposit-override-${b.booking_id}`}>
                      Deposit
                      <input type="number" min={0} max={100} step={5} placeholder={String(defaultPercent(b))}
                        value={overrides[b.booking_id] ?? ""} onChange={(e) => setOverrides({ ...overrides, [b.booking_id]: e.target.value })}
                        data-testid={`deposit-override-input-${b.booking_id}`}
                        className="w-14 rounded-full border border-purple-100 bg-white px-2 py-1 text-xs text-center" />
                      %
                    </label>
                    <button onClick={() => act(b.booking_id, "decline")} data-testid={`decline-${b.booking_id}`}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-red-200 text-red-600 text-xs hover:bg-red-50 transition-colors">
                      <X size={13} /> Decline
                    </button>
                  </>
                )}
                <button onClick={() => { setRescheduling(rescheduling === b.booking_id ? null : b.booking_id); setRDate(b.date); setRTime(b.time); }}
                  data-testid={`reschedule-toggle-${b.booking_id}`}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-purple-200 text-purple-700 text-xs hover:border-gold transition-colors">
                  <CalendarClock size={13} /> Reschedule
                </button>
                {b.status === "approved" && (
                  <button onClick={() => act(b.booking_id, "complete")} data-testid={`complete-${b.booking_id}`}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-purple-700 text-white text-xs hover:bg-purple-800 transition-colors">
                    <CheckCircle2 size={13} /> Mark complete
                  </button>
                )}
              </div>
            )}

            {rescheduling === b.booking_id && (
              <div className="flex flex-wrap items-end gap-3 mt-4 bg-lilac-mist rounded-2xl p-4" data-testid={`reschedule-form-${b.booking_id}`}>
                <input type="date" value={rDate} onChange={(e) => setRDate(e.target.value)} data-testid={`reschedule-date-${b.booking_id}`}
                  className="rounded-xl border border-purple-100 px-3 py-2 text-sm" />
                <input type="time" value={rTime} onChange={(e) => setRTime(e.target.value)} data-testid={`reschedule-time-${b.booking_id}`}
                  className="rounded-xl border border-purple-100 px-3 py-2 text-sm" />
                <button onClick={() => act(b.booking_id, "reschedule", { date: rDate, time: rTime })} data-testid={`reschedule-confirm-${b.booking_id}`}
                  className="px-5 py-2 rounded-full bg-gold text-obsidian text-xs font-semibold hover:brightness-110 transition-all">
                  Confirm new time
                </button>
              </div>
            )}
          </FadeUp>
        ))}
        {shown.length === 0 && <p className="text-sm text-slate-400 italic">No {filter} bookings.</p>}
      </div>
    </div>
  );
}
